import React, { useEffect, useState, useCallback } from "react";
import api, { describeError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge, PriorityBadge } from "@/components/common/StatusBadge";
import { LoadingState, EmptyState } from "@/components/common/EmptyState";
import TaskDialog from "@/pages/TaskDialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PRIORITIES, fmtDate } from "@/lib/helpers";
import { toast } from "sonner";
import { Plus, ListChecks, AlertCircle, Pencil } from "lucide-react";

const VIEWS = [
  { key: "all", label: "All" }, { key: "mine", label: "My Tasks" }, { key: "today", label: "Due Today" },
  { key: "upcoming", label: "Upcoming" }, { key: "overdue", label: "Overdue" }, { key: "completed", label: "Completed" },
];

export default function Tasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState(null);
  const [view, setView] = useState("all");
  const [priority, setPriority] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => { const { data } = await api.get("/tasks"); setTasks(data); }, []);
  useEffect(() => { load(); }, [load]);

  const today = new Date().toISOString().slice(0, 10);
  const filtered = (tasks || []).filter((t) => {
    if (priority !== "all" && t.priority !== priority) return false;
    if (view === "mine") return t.advocate_id === user.id;
    if (view === "today") return t.due_date === today && t.status !== "completed";
    if (view === "upcoming") return t.due_date && t.due_date > today && t.status !== "completed";
    if (view === "overdue") return t.overdue;
    if (view === "completed") return t.status === "completed";
    return true;
  });

  const setStatus = async (t, status) => {
    try { await api.put(`/tasks/${t.id}`, { ...t, status }); load(); }
    catch (err) { toast.error(describeError(err)); }
  };

  const overdueCount = (tasks || []).filter((t) => t.overdue).length;

  return (
    <div data-testid="tasks-page">
      <PageHeader title="Tasks" subtitle="Assign, track and complete practice work."
        actions={<Button onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-task-btn" className="bg-gold text-navy hover:bg-gold-dark"><Plus className="mr-1.5 h-4 w-4" /> Add Task</Button>} />

      {overdueCount > 0 && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" data-testid="overdue-banner">
          <AlertCircle className="h-4 w-4" /> You have {overdueCount} overdue task{overdueCount === 1 ? "" : "s"}.
        </div>
      )}

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Tabs value={view} onValueChange={setView}>
          <TabsList className="flex flex-wrap justify-start gap-1 bg-secondary/60">
            {VIEWS.map((v) => <TabsTrigger key={v.key} value={v.key} data-testid={`task-view-${v.key}`}>{v.label}</TabsTrigger>)}
          </TabsList>
        </Tabs>
        <Select value={priority} onValueChange={setPriority}>
          <SelectTrigger className="w-[150px]" data-testid="task-priority-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Priorities</SelectItem>{PRIORITIES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {!tasks ? <LoadingState /> : filtered.length === 0 ? (
        <EmptyState icon={ListChecks} title="No tasks here" description="Create a task or switch views." testid="tasks-empty"
          action={<Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-navy text-white hover:bg-navy-light"><Plus className="mr-1.5 h-4 w-4" /> Add Task</Button>} />
      ) : (
        <Card className="divide-y divide-border border-border shadow-sm">
          {filtered.map((t) => (
            <div key={t.id} data-testid={`task-row-${t.id}`} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center">
              <div className="min-w-0 flex-1">
                <p className={`text-sm font-medium ${t.status === "completed" ? "text-muted-foreground line-through" : "text-navy"}`}>{t.title}</p>
                <p className="text-xs text-muted-foreground">
                  {t.case_title ? `${t.case_title} · ` : ""}{t.advocate_name ? `${t.advocate_name} · ` : ""}Due {fmtDate(t.due_date)}{t.due_time ? ` ${t.due_time}` : ""}{t.overdue ? " · Overdue" : ""}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <PriorityBadge priority={t.priority} />
                <StatusBadge status={t.status} />
                {t.status === "todo" && <Button size="sm" variant="outline" onClick={() => setStatus(t, "in_progress")} data-testid={`task-start-${t.id}`}>Start</Button>}
                {t.status !== "completed" && t.status !== "cancelled" && <Button size="sm" onClick={() => setStatus(t, "completed")} data-testid={`task-complete-${t.id}`} className="bg-emerald-600 text-white hover:bg-emerald-700">Complete</Button>}
                {(t.status === "completed" || t.status === "cancelled") && <Button size="sm" variant="outline" onClick={() => setStatus(t, "todo")} data-testid={`task-reopen-${t.id}`}>Reopen</Button>}
                {t.status !== "completed" && t.status !== "cancelled" && <Button size="sm" variant="outline" onClick={() => setStatus(t, "cancelled")} data-testid={`task-cancel-${t.id}`} className="text-rose-600 hover:bg-rose-50">Cancel</Button>}
                <Button size="sm" variant="outline" onClick={() => { setEditing(t); setDialogOpen(true); }} data-testid={`task-edit-${t.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          ))}
        </Card>
      )}

      <TaskDialog open={dialogOpen} onOpenChange={setDialogOpen} initial={editing} onSaved={load} />
    </div>
  );
}
